import React from 'react';

export type IconButtonVariant = 'edit' | 'delete' | 'view' | 'custom';

interface IconButtonProps {
  variant: IconButtonVariant;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  ariaLabel?: string;
  customIcon?: string;
  className?: string;
}

/**
 * Reusable Icon Button Component
 * Consistent action buttons across admin section
 * 
 * @param variant - Button type (edit, delete, view, custom)
 * @param onClick - Click handler
 * @param disabled - Disable button
 * @param title - Tooltip text
 * @param ariaLabel - Accessibility label
 * @param customIcon - Custom icon class (for variant='custom')
 * @param className - Additional CSS classes
 */
const IconButton: React.FC<IconButtonProps> = ({
  variant,
  onClick,
  disabled = false,
  title,
  ariaLabel,
  customIcon,
  className = '',
}) => {
  // Get icon class based on variant
  const getIconClass = (): string => {
    switch (variant) {
      case 'edit':
        return 'st-pen text-royalblue-100';
      case 'delete':
        return 'st-delete text-red-600';
      case 'view':
        return 'st-eye text-blue-600';
      case 'custom':
        return customIcon || 'st-settings';
      default:
        return 'st-settings';
    }
  };

  // Get hover color based on variant
  const getHoverClass = (): string => {
    switch (variant) {
      case 'edit':
        return 'hover:bg-blue-50';
      case 'delete':
        return 'hover:bg-red-50';
      case 'view':
        return 'hover:bg-blue-50';
      default:
        return 'hover:bg-gray-50';
    }
  };

  // Get default title based on variant
  const getDefaultTitle = (): string => {
    switch (variant) {
      case 'edit':
        return 'Edit';
      case 'delete':
        return 'Delete';
      case 'view':
        return 'View';
      default:
        return 'Action';
    }
  };

  // Get default aria-label based on variant
  const getDefaultAriaLabel = (): string => {
    switch (variant) {
      case 'edit':
        return 'Edit item';
      case 'delete':
        return 'Delete item';
      case 'view':
        return 'View item';
      default:
        return 'Perform action';
    }
  };

  return (
    <button
      className={`w-8 h-8 rounded-md border border-blue-300 ${getHoverClass()} transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      onClick={onClick}
      disabled={disabled}
      title={title || getDefaultTitle()}
      aria-label={ariaLabel || getDefaultAriaLabel()}
      type="button"
    >
      <span className={`${getIconClass()} text-xs`} />
    </button>
  );
};

export default IconButton;

